import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  TextField,
  CircularProgress,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

import { RoundIconButton } from "components/Base/Buttons";
import NoRecordsFound from "../NoRecordsFound";

export interface GenericRecordSelectionProps<T> {
  onSelect: (item: T) => void;
  selectedId?: number | null;
  fetchItems?: () => Promise<T[]>;
  items?: T[];
  AddPopupComponent?: React.ComponentType<{
    open: boolean;
    onClose: () => void;
    onSaved?: (newItem: T) => void;
  }>;
  idKey?: keyof T;
  nameKey?: keyof T | ((item: T) => string);
  searchPlaceholder?: string;
  colorKey?: string;
  onCreated?: (item: T) => void;
  sortKey?: keyof T | ((item: T) => string | number | null | undefined);
}

function GenericRecordSelection<T extends Record<string, any>>({
  onSelect,
  selectedId = null,
  fetchItems,
  items = [],
  AddPopupComponent,
  idKey = "id",
  nameKey = "name",
  searchPlaceholder = "Search...",
  colorKey = "primary",
  onCreated,
  sortKey,
}: GenericRecordSelectionProps<T>) {
  const theme = useTheme();

  const paletteEntry = theme.palette[colorKey as keyof typeof theme.palette];

  const primaryColor =
    typeof paletteEntry === "object" && paletteEntry && "main" in paletteEntry
      ? (paletteEntry as { main: string }).main
      : theme.custom.base.primary.dark;

  const textColor = theme.custom.base.text.primary.dark;

  const [data, setData] = useState<T[]>(items);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);

  const AddPopup = AddPopupComponent;

  const getDisplayName = (item: T) => {
    return typeof nameKey === "function"
      ? nameKey(item)
      : (item[nameKey] as string);
  };

  const getSortValue = (item: T) => {
    if (!sortKey) return getDisplayName(item) ?? "";
    return typeof sortKey === "function" ? sortKey(item) : item[sortKey];
  };

  const sortItems = (list: T[]) => {
    return [...list].sort((a, b) => {
      const aValue = String(getSortValue(a) ?? "").toLowerCase().trim();
      const bValue = String(getSortValue(b) ?? "").toLowerCase().trim();

      return aValue.localeCompare(bValue);
    });
  };

  // Only fetch when fetchItems changes
  useEffect(() => {
    if (!fetchItems) return;

    let isMounted = true;
    setLoading(true);

    fetchItems()
      .then((result) => {
        if (!isMounted) return;
        setData(result);
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [fetchItems]);

  // For static items mode
  useEffect(() => {
    if (!fetchItems) {
      setData(items);
    }
  }, [items, fetchItems]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();

    const filteredResults = data.filter((item) => {
      const name = getDisplayName(item);
      return name?.toLowerCase().includes(q);
    });

    return sortItems(filteredResults);
  }, [data, search, nameKey, sortKey]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={1} mb={2}>
        <TextField
          fullWidth
          size="small"
          label={searchPlaceholder}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{
            "& .MuiInputBase-input": { color: "white" },
            "& .MuiInputLabel-root": { color: "white" },
            "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
              borderColor: "white",
            },
            "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: "white",
            },
            "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
              {
                borderColor: "white",
              },
          }}
          InputLabelProps={{ style: { color: "white" } }}
        />

        {AddPopup && (
          <RoundIconButton
            colour="coloured"
            displaySize="separate"
            colorKey={colorKey}
            iconColor={textColor}
            onClick={() => setOpenDialog(true)}
          />
        )}
      </Box>

      {filtered.length > 0 ? (
        <List>
          {filtered.map((item) => {
            const id = item[idKey] as unknown as number;
            const name = getDisplayName(item);

            return (
              <ListItem key={id} disablePadding>
                <ListItemButton
                  selected={id === selectedId}
                  onClick={() => onSelect(item)}
                  sx={{
                    "&:hover": { color: primaryColor },
                  }}
                >
                  <ListItemText
                    primary={name}
                    primaryTypographyProps={{ style: { color: "white" } }}
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      ) : (
        <NoRecordsFound
          itemName="records"
          minHeight={50}
          maxHeight={100}
        />
        
      )}

      {AddPopup && (
        <AddPopup
          open={openDialog}
          onClose={() => setOpenDialog(false)}
          onSaved={(newItem) => {
            setOpenDialog(false);
            setData((prev) => [...prev, newItem]);
            onCreated?.(newItem);
          }}
        />
      )}
    </Box>
  );
}

export default GenericRecordSelection;